import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export interface DemoVideoSettings {
  url: string;
  enabled: boolean;
}

export function useLandingSettings() {
  return useQuery({
    queryKey: ["landing-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("landing_settings")
        .select("*");
      
      if (error) throw error;
      return data;
    },
  });
}

export function useDemoVideoSettings() {
  return useQuery({
    queryKey: ["landing-settings", "demo_video"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("landing_settings")
        .select("value")
        .eq("key", "demo_video")
        .single();
      
      if (error) {
        if (error.code === "PGRST116") {
          return { url: "", enabled: false } as DemoVideoSettings;
        }
        throw error;
      }
      
      // Parse the JSONB value
      const value = data.value as unknown as DemoVideoSettings;
      return value;
    },
  });
}

export function useUpdateDemoVideoSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: DemoVideoSettings) => {
      const { data: existing } = await supabase
        .from("landing_settings")
        .select("id")
        .eq("key", "demo_video")
        .single();
      
      // Convert to Json type
      const jsonValue: Json = {
        url: settings.url,
        enabled: settings.enabled,
      };
      
      if (existing) {
        const { error } = await supabase
          .from("landing_settings")
          .update({ 
            value: jsonValue,
            updated_at: new Date().toISOString() 
          })
          .eq("key", "demo_video");
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("landing_settings")
          .insert({ 
            key: "demo_video", 
            value: jsonValue
          });
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-settings"] });
    },
  });
}
